import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MedOptionType } from "@mk/med-ui";
import { catchError } from "rxjs";
import { RecipientSeriesController } from "@app/ui_controllers/recipientSeriesController";
import { DistributionDozRecipientDto, DistributionPageDto } from "@app/ui_models/web-response";
import { DistributionGoodDozDto, SeriesSpecificationDozDto } from "@app/ui_models/model-dto";
import { MedNotificationService } from "mk/med-notification";
import { SubDistributionDozController } from "@app/ui_controllers/subDistributionDozController";

@Component({
  selector: "app-seriesDoz",
  templateUrl: "./seriesDoz.component.html",
  styleUrls: ["/seriesDoz.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class SeriesDozComponent implements OnInit {

  constructor(private router: Router,
              private route: ActivatedRoute,
              private  recipientSeriesController: RecipientSeriesController,
              private subDistributionController: SubDistributionDozController,
              private readonly notificationService: MedNotificationService) {
  }

  optionsTabGroup: MedOptionType<DistributionGoodDozDto>[] = [];
  tabHeader: { label: string; key: string;  }[] = tabHeaderSeries;
  subDistribution: DistributionPageDto | null = null;
  activeDistributionRecipients: DistributionDozRecipientDto[] = [];
  currentRegionRecipientId: number | null = null;
  currentRecipientId: number | null = null;
  activeIndex: any = -1;
  distributionId = -1;
  subDistributionId = -1;
  rows: DistributionGoodDozDto[] = [];
  addSeriesModal = false;
  allSeries: SeriesSpecificationDozDto[] = [];
  selectedSeries: SeriesSpecificationDozDto[] = [];
  activeSelectedSeries: SeriesSpecificationDozDto[] = [];
  quantitiesToUpdate: {distributionRecipientSpecificationId: number; quantity: number; }[] = [];
  activeDistributionGoodId: number | undefined | null = 0;
  optionsModel: DistributionGoodDozDto | null = null;
  protected readonly alert = alert;

  ngOnInit(): void {
    const param: string|null = this.route.snapshot.paramMap.get("id");
    if (param !== null) {
      this.distributionId = Number.parseInt(param.split("&")[0], 10);
      this.subDistributionId = Number.parseInt(param.split("&")[1], 10);
      this.getSubDistribution();
    }
  }

  getSubDistribution(): void {
    this.subDistributionController.getSubDistribution(this.subDistributionId).subscribe(data => {
      if (data["WarnMessage"]) {
        this.notificationService.info(data["WarnMessage"]);
      }
      if (data["subDistribution"]) {
        this.subDistribution = data["subDistribution"] as DistributionPageDto;
      }
      if (data["Data"]) {
        this.rows = data["Data"] as DistributionGoodDozDto[];
      }

      this.activeDistributionGoodId = this.rows[0].distributionGoodsId;
      this.activeDistributionRecipients = this.rows[0].recipients;
      this.currentRegionRecipientId = this.rows[0].regionRecipientId;
      this.optionsTabGroup = this.rows.map((distr: DistributionGoodDozDto) => ({
        value: distr,
        label: distr.mnnName,
      }));
      this.optionsModel = this.optionsTabGroup[0].value;
      this.getAllSeries();
    });
  }

  getAllSeries() {
    if (this.currentRegionRecipientId) {
      this.recipientSeriesController.getDistributionDozSeries(this.currentRegionRecipientId).subscribe(data => {
        this.allSeries = data;
      });
    }
  }

  goToDistribution() {
    this.router.navigate([`sub/distribution/doz/view/${this.distributionId}&${this.subDistributionId}`]);
  }

  tabGroupChanged($event: DistributionGoodDozDto | null): void {
    this.activeDistributionGoodId = $event?.distributionGoodsId;
    const matchedRow = this.rows.find(row => row.distributionGoodsId === this.activeDistributionGoodId);
    if (matchedRow) {
      this.activeDistributionRecipients = matchedRow.recipients;
      this.currentRecipientId = null;
      this.currentRegionRecipientId = matchedRow.regionRecipientId;
    } else {
      this.activeDistributionRecipients = [];
      this.currentRegionRecipientId = null;
    }
    this.activeIndex = -1;
    this.getAllSeries();
  }

  getHeaderClass(index: number): string {
    let className = "";
    if (index === this.activeIndex) {
      className = "purple-10-background";
    }
    return className;
  }

  getCurrentRegionName(): string {
    const recipient = this.activeDistributionRecipients?.find(recipient => recipient.id === this.currentRecipientId);
    return recipient ? recipient.recipientName : "";
  }

  loadSelectedSeries(recipientId: number | null) {
    if (!recipientId) {
      return;
    }
    this.recipientSeriesController.getDistributionDozSeriesByRecipient(recipientId).subscribe(data => {
      this.selectedSeries = data;
    });
  }

  openAddSeriesModal() {
    this.addSeriesModal = true;
    if (this.selectedSeries) {
      this.activeSelectedSeries = this.allSeries.filter(series =>
        this.selectedSeries.some(s => s.seriesCatalogue === series.seriesCatalogue),
      );
      this.activeSelectedSeries.forEach(series => {
        const match = this.selectedSeries.find(s => s.seriesCatalogue === series.seriesCatalogue);
        if (match) {
          (series as any).quantity = match.quantity ?? 0;
          (series as any).distributionRecipientSeriesId = match.distributionRecipientSeriesId ?? -1;
        }
      });
    }
  }

  onAccordionOpen(event: any): void {
    this.quantitiesToUpdate = [];
    const { index } = event;
    this.activeIndex = index;
    const recipient = this.activeDistributionRecipients?.[index];
    if (recipient?.id) {
      this.currentRecipientId = recipient?.id;
      if (this.currentRecipientId) {
        this.loadSelectedSeries(this.currentRecipientId);
      }
    }
  }

  onQuantityChange(distributionRecipientSpecificationId: number, quantity: number): void {
    const existing = this.quantitiesToUpdate.find(quantity => quantity.distributionRecipientSpecificationId === distributionRecipientSpecificationId);
    if (existing) {
      existing.quantity = quantity;
    } else {
      this.quantitiesToUpdate.push({ distributionRecipientSpecificationId, quantity });
    }
  }

  updateDistributedQunatity() {
    const newQuantity = this.selectedSeries.reduce((sum, item) => sum + Number(item.quantity), 0);
    const currentRecipient = this.activeDistributionRecipients.find(item => item.id === this.currentRecipientId);
    if (currentRecipient) {
      currentRecipient.distributedSeriesQuantity = newQuantity;
    }
  }

  accept(): void {
    const seriesByRecepient = this.activeSelectedSeries.map(item => ({
      specificationId: item.specificationId,
    }));
    if (this.currentRecipientId) {
      this.recipientSeriesController.createSeriesByRecipientDoz(this.currentRecipientId, seriesByRecepient)
        .pipe(
          catchError(response => {
            this.notificationService.error("Помилка при збереженні серій",
            );
            throw response;
          }),
        )
        .subscribe(() => {
          this.addSeriesModal = false;
          this.loadSelectedSeries(this.currentRecipientId);
        });
    }
  }

  update(): void {
    if (this.quantitiesToUpdate.length > 0) {
      this.recipientSeriesController.updateQuntityDoz(this.quantitiesToUpdate)
        .pipe(
          catchError(response => {
            this.notificationService.error("Помилка при зміні кількості",
            );
            throw response;
          }),
        )
        .subscribe(() => {
          this.notificationService.success("Кількість успішно змінено");
          this.updateDistributedQunatity();
        });
    }
  }

  deleteSeries(id: number | null): void {
    if (id === null) {
      return;
    }
    this.recipientSeriesController.deleteSeriesDoz(id)
      .pipe(
        catchError(response => {
          this.notificationService.error("Помилка при видаленні");
          throw response;
        }),
      )
      .subscribe(() => {
        this.notificationService.success("Успішно видалено");
        this.selectedSeries = this.selectedSeries.filter(
          series => series.distributionRecipientSpecificationId !== id,
        );
        this.updateDistributedQunatity();
      });
  }
}

export const tabHeaderSeries = [
  { key: "1", label: "Серія/Каталожний номер" },
  { key: "2", label: "Дата виробництва" },
  { key: "3", label: "Термін придатності" },
  { key: "4", label: "Загальна кількість" },
  { key: "5", label: "Зарезервована кількість" },
  { key: "6", label: "Доступна кількість" },
  { key: "7", label: "Кількість серій" },
  { key: "8", label: "Розподілити" },
  { key: "9", label: "" },
];
